import { useMutation, useQuery, useQueryClient } from 'react-query';
import axiosInstance from '../config/Axios';
import { useCustomToast } from '../pages/Contexts/ToastContext';

/**
 * Phase 7 — Video Wall dashboard overview: all walls grouped by location with
 * state, device count, group, and schedule now/next. Live sync/cache health is
 * layered on the client from RTDB (useWallSyncTelemetry) per wall row.
 *   GET /video-walls/overview → { locations: [{ location_id, location, timezone, walls[] }] }
 * Backend wraps in a resource ({ data: { status, ... } }); unwrap with `res?.data ?? res`.
 */
const OVERVIEW_KEY = 'wallOverview';

export const useWallOverview = () =>
  useQuery(
    OVERVIEW_KEY,
    async () => {
      const res = (await axiosInstance.get('/video-walls/overview')).data;
      return (res?.data ?? res)?.locations ?? [];
    },
    { staleTime: 10_000, refetchInterval: 30_000 },
  );

// Location-level fan-out ops (start/stop/refresh every wall at a location).
const locOp = (verb, okMsg) => () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((id) => axiosInstance.post(`/locations/${id}/${verb}`), {
    onSuccess: (res) => {
      qc.invalidateQueries(OVERVIEW_KEY);
      const body = res?.data?.data ?? res?.data;
      showToast('success', 'Locations', body?.msg || okMsg);
    },
    onError: (err) => showToast('error', 'Locations', err.response?.data?.message),
  });
};

export const useLocationStart   = locOp('start', 'Started.');
export const useLocationStop    = locOp('stop', 'Stopped.');
export const useLocationRefresh = locOp('refresh', 'Refreshed.');
